import instance from "../api/axios";
import { toast } from "react-toastify";

const TOAST = {
  position: "top-right",
  autoClose: 4000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  progress: undefined,
  theme: "light",
};

/**
 * Deleting is destructive, so it is limited to the two roles the API itself
 * trusts with DELETE /auth/delete-admin. Widen this list to grant it to more
 * roles - every delete button in the dashboard reads from here.
 */
export const DELETE_ROLES = ["Super Admin", "CEO"];

export const canDelete = () =>
  DELETE_ROLES.includes(sessionStorage.getItem("admin_role_validation"));

/**
 * Most delete routes are wrapped in `try { ... } catch (err) { res.send(err) }`,
 * which answers 200 with a serialised error instead of a 4xx. So a resolved
 * promise is not proof of anything - the body has to be inspected.
 *
 * Two success shapes are in use:
 *   { success: true, message, deletedX }   auth, teams, blog, practice, contact
 *   the raw deleted mongoose document      event, certificate
 */
const succeeded = (data) => {
  if (!data) return false;
  if (data.success === false) return false;
  if (data.success === true) {
    // /contact/delete-contact answers success:true with a null document when the
    // id matched nothing, because its controller never asserts the record
    // existed. An explicitly null payload means nothing was deleted.
    const payload = Object.entries(data).find(
      ([key]) => key !== "success" && key !== "message"
    );
    return payload ? payload[1] !== null : true;
  }
  return Boolean(data._id); // raw deleted document
};

/**
 * Run a delete request and report the outcome.
 *
 *   await deleteRecord({ url: `/blog/delete-blog/${id}`, label: "Blog" })
 *   await deleteRecord({ url: "/auth/delete-admin", body: { id }, label: "Employee" })
 *
 * Returns true only when the record is actually gone.
 */
export const deleteRecord = async ({ url, body, label = "Record" }) => {
  try {
    const res = await instance({
      url,
      method: "DELETE",
      // Several routes read the id from req.body rather than a URL param.
      ...(body ? { data: body } : {}),
    });

    if (!succeeded(res?.data)) {
      toast.error(
        res?.data?.success === true
          ? // The API reported success but removed nothing - the record was
            // already gone. Do not echo its "deleted successfully" message.
            `${label} no longer exists. Refreshing the list.`
          : res?.data?.message ||
              `${label} could not be deleted. You may not have permission.`,
        TOAST
      );
      return false;
    }

    toast.success(`${label} deleted successfully`, TOAST);
    return true;
  } catch (e) {
    toast.error(
      e?.response?.data?.error || `${label} could not be deleted.`,
      TOAST
    );
    return false;
  }
};
